class BackToTopButton extends HTMLElement {
  /** @type {number} */
  #threshold = 400;

  /** @type {() => void} */
  #onScroll = () => {
    this.#updateVisibility();
  };

  connectedCallback() {
    this.#threshold = Number(this.dataset.threshold) || 400;

    const button = this.querySelector('button');
    button?.addEventListener('click', () => this.#scrollToTop());

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#updateVisibility();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
  }

  #updateVisibility() {
    const isVisible = window.scrollY > this.#threshold;
    this.toggleAttribute('visible', isVisible);

    const button = this.querySelector('button');
    if (!button) return;

    button.setAttribute('tabindex', isVisible ? '0' : '-1');
    button.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
  }

  #scrollToTop() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? 'instant' : 'smooth',
    });

    this.#moveFocusToMain(prefersReducedMotion);
  }

  /**
   * Moves keyboard focus to the main content landmark after scrolling up.
   * @param {boolean} instantScroll
   */
  #moveFocusToMain(instantScroll) {
    const main = document.getElementById('MainContent');
    if (!main) return;

    const focusMain = () => {
      if (!main.hasAttribute('tabindex')) {
        main.setAttribute('tabindex', '-1');
      }

      main.focus({ preventScroll: true });
    };

    if (instantScroll) {
      focusMain();
      return;
    }

    if ('onscrollend' in window) {
      window.addEventListener('scrollend', focusMain, { once: true });
      return;
    }

    window.setTimeout(focusMain, 600);
  }
}

if (!customElements.get('back-to-top-button')) {
  customElements.define('back-to-top-button', BackToTopButton);
}
